import React, { useState } from 'react';
import { PRODUCT_PAYMENT_STYLE } from '../money/refundTransfer/refundShared';

// Supervisor's helper calculator — builds the support amount from the linked
// order's REAL charged components instead of mental math. Business rules
// (confirmed with Gain):
//   - ค่าสินค้า       = amount × qty (¥) → baht at the order's productRate
//   - ค่าส่งญี่ปุ่น    = deliveryFeeJp (¥) → codRate when isCod, else productRate
//   - ค่าน้ำหนัก      = weightPrice if set, else weight × weightRate (฿)
//   - ค่าธรรมเนียม    = transactionFee (฿)
//   - ค่าส่งไทย       = deliveryFeeTh (฿)
// Rates come snapshotted from the order (what the customer actually paid).
// Nothing is stored from here — "ใช้ยอดนี้" writes the final baht into the
// decision form and appends the breakdown to the message, so the RULE is
// permanently recorded in the thread.

const round2 = (n) => Math.round(n * 100) / 100;
const fmt = (n) => Number(n).toLocaleString('th-TH', { maximumFractionDigits: 2 });

// Payment status per component — critical when deciding support: don't refund
// what was never paid. productPayment/shipJpPaid are Thai status strings (can be
// half-paid มัดจำ 50%); the rest are booleans.
const payBadge = (value) => {
  if (value === true || value === 'true') {
    return { label: 'จ่ายแล้ว', cls: 'bg-green-50 text-green-700 border-green-200' };
  }
  if (!value || value === 'false') {
    return { label: 'ยังไม่จ่าย', cls: 'bg-gray-50 text-gray-500 border-gray-200' };
  }
  return { label: value, cls: PRODUCT_PAYMENT_STYLE[value] || 'bg-gray-50 text-gray-500 border-gray-200' };
};

const buildRows = (order) => {
  if (!order) return [];
  const productYen = (parseFloat(order.amount) || 0) * (parseInt(order.qty, 10) || 1);
  const jpFeeYen = parseFloat(order.deliveryFeeJp) || 0;
  const jpFeeRate = order.isCod ? (parseFloat(order.codRate) || 0) : (parseFloat(order.productRate) || 0);
  const weightBaht = (parseFloat(order.weightPrice) || 0) > 0
    ? parseFloat(order.weightPrice)
    : (parseFloat(order.weight) || 0) * (parseFloat(order.weightRate) || 0);

  return [
    { key: 'product', label: 'ค่าสินค้า', yen: productYen, rate: parseFloat(order.productRate) || 0, pay: payBadge(order.productPayment) },
    { key: 'txFee', label: 'ค่าธรรมเนียม', baht: parseFloat(order.transactionFee) || 0, pay: payBadge(order.transactionFeePaid) },
    { key: 'jpFee', label: 'ค่าส่งญี่ปุ่น', yen: jpFeeYen, rate: jpFeeRate, pay: payBadge(order.shipJpPaid) },
    { key: 'weight', label: 'ค่าน้ำหนัก', baht: weightBaht, pay: payBadge(order.weightPaid) },
    { key: 'thFee', label: 'ค่าส่งไทย', baht: parseFloat(order.deliveryFeeTh) || 0, pay: payBadge(order.shipThPaid) },
  ]
    .map((r) => ({ ...r, baht: round2(r.yen != null ? r.yen * r.rate : r.baht) }))
    .filter((r) => r.baht > 0);
};

const RefundCalculator = ({ order, onApply }) => {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState({});
  const [percent, setPercent] = useState('100');

  const rows = buildRows(order);
  if (rows.length === 0) return null;

  const toggle = (key) => setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  const selected = rows.filter((r) => checked[r.key]);
  const base = round2(selected.reduce((sum, r) => sum + r.baht, 0));
  const pct = parseFloat(percent) || 0;
  const result = round2(base * pct / 100);

  const apply = () => {
    const parts = selected.map((r) =>
      r.yen != null ? `${r.label} ¥${fmt(r.yen)}×${r.rate}` : `${r.label} ฿${fmt(r.baht)}`
    ).join(' + ');
    const text = `🧮 ช่วยเหลือ ${pct}% ของ (${parts}) = ฿${fmt(result)}`;
    onApply(result, text);
    setOpen(false);
  };

  return (
    <div className="mb-3">
      <button type="button" onClick={() => setOpen(!open)}
        className="text-sm text-purple-700 border border-purple-300 bg-white hover:bg-purple-100 px-3 py-1.5 rounded-lg">
        🧮 คำนวณจากออเดอร์ {open ? '▲' : '▼'}
      </button>

      {open && (
        <div className="mt-2 p-3 bg-white border border-purple-200 rounded-lg">
          <p className="text-xs text-gray-500 mb-2">ติ๊กรายการที่จะช่วยเหลือ (ตัวเลข/เรทตามที่ลูกค้าถูกเก็บจริงในออเดอร์)</p>
          {rows.map((r) => (
            <label key={r.key} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
              <input type="checkbox" checked={!!checked[r.key]} onChange={() => toggle(r.key)} />
              <span>{r.label}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded border ${r.pay.cls}`}>{r.pay.label}</span>
              <span className="text-gray-500 text-xs ml-auto">
                {r.yen != null ? `¥${fmt(r.yen)} × ${r.rate} = ` : ''}
              </span>
              <span className="font-medium">฿{fmt(r.baht)}</span>
            </label>
          ))}

          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 text-sm">
            <span>ช่วยเหลือ</span>
            <input type="text" inputMode="decimal" value={percent}
              onChange={(e) => setPercent(e.target.value.replace(/[^\d.]/g, ''))}
              className="w-16 border border-gray-300 rounded px-2 py-1 text-right" />
            <span>% ของ ฿{fmt(base)}</span>
            <span className="ml-auto font-bold text-purple-800">= ฿{fmt(result)}</span>
          </div>

          <button type="button" onClick={apply} disabled={result <= 0}
            className="mt-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
            ใช้ยอดนี้ →
          </button>
        </div>
      )}
    </div>
  );
};

export default RefundCalculator;
